import { Box, Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import {
  AtsScheduleInterviewConnectedCalendarsDocument,
  ScheduleInterviewApplicationFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { localTimezone } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ConnectCalendarModal } from 'modules/ats/components/ConnectCalendarModal'

import { ApplicationCard } from './ApplicationCard'
import { CreateInterviewData } from './CreateInterviewData'
import { DateTimeStep, isDateTimeCompleted } from './DateTimeStep'
import { MessageStep, isMessageCompleted } from './MessageStep'
import { ReviewStep, isReviewCompleted } from './ReviewStep'
import { TeamLocationStep, isTeamLocationCompleted } from './TeamLocationStep'
import { useCreateInterview } from './useCreateInterview'

interface Props {
  application: ScheduleInterviewApplicationFragment
  onClose: () => void
}

export const ScheduleInterview = ({ application, onClose }: Props) => {
  const { company, access } = useCurrentSession()
  const { create, loading: creatingInterview } = useCreateInterview()
  const { data: calendarData, loading } = useQuery(AtsScheduleInterviewConnectedCalendarsDocument)

  const calendarSyncs = calendarData?.cronofyScheduling.calendarSyncsConnection.nodes || []
  const accessHasCalendar = calendarSyncs.some((sync) => sync.accessId === access.id)
  const hasCalendars = calendarSyncs.length > 0

  const form = useForm<CreateInterviewData>({
    defaultValues: {
      attendeeIds: [access.id],
      interviewType: 'online',
      customLocation: '',
      date: '',
      locale: access.locale || 'en',
      timezone: localTimezone() || 'Etc/UTC', //TO-DO: get this from company locations
      minutes: 60,
      messageContent: '',
      messageAttachments: [],
      organizerAccessId: access.id,
    },
    onSubmit: async (data) => {
      if (creatingInterview) return Promise.reject()

      if (!data.date || !data.minutes || !data.interviewType) {
        return Promise.reject('Missing required fields')
      }

      const endsAt = new Date(data.date)
      endsAt.setMinutes(endsAt.getMinutes() + data.minutes)

      const result = await create({
        companyId: company.id,
        atsApplicationId: application.id,
        organizerAccessId: data.organizerAccessId ?? 0,
        attendeeIds: data.attendeeIds,
        interviewType: data.interviewType,
        workspaceId: data.workspaceId,
        customLocation: data.customLocation,
        startsAt: data.date,
        endsAt: endsAt.toISOString(),
        timezone: data.timezone,
        messageContent: data.messageContent,
        messageAttachments: data.messageAttachments,
      })

      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
        form.resetForm()
      }

      return handled
    },
  })

  const formHandleSubmit = form.handleSubmit
  const onSubmit = useUnsafeCallback(() => formHandleSubmit(), [formHandleSubmit])

  useEffect(() => {
    if (
      hasCalendars &&
      !form.values.organizerAccessId &&
      calendarSyncs.some((calendarSync) => calendarSync.accessId === access.id)
    ) {
      form.setValues({
        ...form.values,
        organizerAccessId: access.id,
      })
    }
  }, [hasCalendars])

  const steps = useMemo(() => {
    if (!hasCalendars) return []

    const calendarSyncsByAccessId = calendarSyncs.reduce(
      (obj, calendarSync) => Object.assign(obj, { [calendarSync.accessId]: calendarSync }),
      {}
    )

    const renderStep = (children: JSX.Element) => (
      <Box paddingRight="s16" gap="s24" overflowX="hidden">
        <ApplicationCard application={application} />
        <Box paddingBottom="s32">{children}</Box>
      </Box>
    )

    const currentSteps = [
      {
        title: i18n.t('ats.schedule_interview.steps.team_location.title'),
        nextText: i18n.t('ats.schedule_interview.steps.next'),
        render: (props: WizardStepRenderParams<CreateInterviewData>) =>
          renderStep(
            <TeamLocationStep {...props} calendarSyncsByAccessId={calendarSyncsByAccessId} />
          ),
        isCompleted: (data: CreateInterviewData) => isTeamLocationCompleted(data),
      },
      {
        title: i18n.t('ats.schedule_interview.steps.date_time.title'),
        nextText: i18n.t('ats.schedule_interview.steps.next'),
        render: (props: WizardStepRenderParams<CreateInterviewData>) =>
          renderStep(<DateTimeStep {...props} calendarSyncsByAccessId={calendarSyncsByAccessId} />),
        isCompleted: (data: CreateInterviewData) => isDateTimeCompleted(data),
      },
      {
        title: i18n.t('ats.schedule_interview.steps.review.title'),
        nextText: i18n.t('ats.schedule_interview.steps.save_interview'),
        render: (props: WizardStepRenderParams<CreateInterviewData>) =>
          renderStep(<ReviewStep {...props} />),
        isCompleted: () => isReviewCompleted(),
      },
    ]

    application.candidate?.email &&
      currentSteps.splice(2, 0, {
        title: i18n.t('ats.schedule_interview.steps.message.title'),
        nextText: i18n.t('ats.schedule_interview.steps.next'),
        render: (props: WizardStepRenderParams<CreateInterviewData>) =>
          renderStep(<MessageStep {...props} />),
        isCompleted: (data: CreateInterviewData) => isMessageCompleted(data),
      })

    return currentSteps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCalendars])

  if (loading) {
    return null
  }

  return (
    <>
      {accessHasCalendar ? (
        <Wizard.Modal
          icon="calendar"
          title={i18n.t('ats.schedule_interview.title')}
          onClose={onClose}
          onSubmit={onSubmit}
          steps={steps}
          initialData={form.initialValues}
          onDataChanged={form.setValues}
        />
      ) : (
        <ConnectCalendarModal onClose={onClose} />
      )}
    </>
  )
}
